import React, { useState } from "react";
import { 
  FaDollarSign, 
  FaShoppingCart,
  FaMoneyBillWave,
  FaCreditCard,
  FaHourglassHalf,
  FaClipboardList,
  FaClock,
  FaCheckCircle,
  FaTruck,
  FaBoxOpen,
  FaTimesCircle
} from 'react-icons/fa';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import FormFilterDate from "../../components/common/FormFilterDate";
import { getDashboardData } from "../../services/reportsService";
import { toastError } from "../../components/common/Toast";
import "../../styles/pages/DashboardHomePage.css";

function DashboardHomePage() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleDateFilter = async (data) => {
    try {
      setLoading(true);
      const response = await getDashboardData(data.date_from, data.date_to);
      
      if (response?.success && response?.data) {
        setDashboardData(response.data);
      } else {
        toastError('No se pudieron obtener los datos del dashboard');
      }
    } catch (error) {
      console.error('Error cargando dashboard:', error);
      toastError('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p className="label" style={{ margin: 0, fontWeight: 'bold' }}>
            {new Date(payload[0].payload.date).toLocaleDateString('es-AR')}
          </p>
          <p style={{ margin: '5px 0 0 0', color: '#4169E1' }}>
            Ventas: {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Iconos y colores por estado (puedes personalizarlos)
  const getStatusStyle = (statusDescription) => {
    const statusUpper = statusDescription.toUpperCase();
    
    // Mapeo exacto primero
    const exactMap = {
      'PENDIENTE': { icon: FaClock, color: 'warning' },
      'PEDIR A TALLER': { icon: FaClipboardList, color: 'info' },
      'EN PRODUCCIÓN': { icon: FaBoxOpen, color: 'primary' },
      'PRODUCCIÓN': { icon: FaBoxOpen, color: 'primary' },
      'TERMINADO': { icon: FaCheckCircle, color: 'success' },
      'COMPLETADO': { icon: FaCheckCircle, color: 'success' },
      'DESPACHADO': { icon: FaTruck, color: 'success' },
      'ENTREGADO': { icon: FaTruck, color: 'success' },
      'CANCELADO': { icon: FaTimesCircle, color: 'danger' },
      'TEST': { icon: FaShoppingCart, color: 'secondary' }
    };

    if (exactMap[statusUpper]) {
      return exactMap[statusUpper];
    }

    // Buscar coincidencia parcial
    const key = Object.keys(exactMap).find(k => statusUpper.includes(k));
    return exactMap[key] || { icon: FaShoppingCart, color: 'secondary' };
  };

  return (
    <div className="dashboard-home-page">
      <div className="mb-4">
        <h2 className="page-title">Dashboard</h2>
        <p className="text-muted">Vista general del sistema</p>
      </div>

      {/* Filtro de fechas */}
      <FormFilterDate handleInfo={handleDateFilter} />

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando datos...</p>
        </div>
      )}

      {!loading && dashboardData && (
        <>
          {/* Cards principales */}
          <div className="row mt-4 g-3">
            {/* Total Ventas */}
            <div className="col-12 col-sm-6 col-lg-4">
              <div className="stats-card">
                <div className="stats-icon bg-success">
                  <FaDollarSign />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Ventas Totales</h6>
                  <h3 className="stats-value">{formatCurrency(dashboardData.total_sales)}</h3>
                  <small className="text-muted">{dashboardData.total_orders} pedidos</small>
                </div>
              </div>
            </div>

            {/* Acreditado en Caja */}
            <div className="col-12 col-sm-6 col-lg-4">
              <div className="stats-card">
                <div className="stats-icon bg-primary">
                  <FaMoneyBillWave />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Acreditado en Caja</h6>
                  <h3 className="stats-value">{formatCurrency(dashboardData.cash_flow.total_credited)}</h3>
                  <small className="text-muted">Efectivo en caja</small>
                </div>
              </div>
            </div>

            {/* Pendiente Acreditación */}
            <div className="col-12 col-sm-6 col-lg-4">
              <div className="stats-card">
                <div className="stats-icon bg-warning">
                  <FaHourglassHalf />
                </div>
                <div className="stats-content">
                  <h6 className="stats-label">Pendiente Acreditación</h6>
                  <h3 className="stats-value">{formatCurrency(dashboardData.cash_flow.pending_accreditation)}</h3>
                  <small className="text-muted">Por acreditar</small>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de ventas por fecha */}
          {dashboardData.sales_by_date && dashboardData.sales_by_date.length > 0 && (
            <div className="row mt-4">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title mb-4">Detalle de ventas</h5>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart
                        data={dashboardData.sales_by_date}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                          stroke="#666"
                          style={{ fontSize: '12px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                          type="monotone" 
                          dataKey="sales" 
                          stroke="#4169E1" 
                          strokeWidth={2}
                          dot={{ fill: '#4169E1', r: 4 }}
                          activeDot={{ r: 6 }}
                          fill="url(#colorSales)"
                        />
                        <defs>
                          <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4169E1" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#4169E1" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cards dinámicas por estado */}
          <div className="row mt-4">
            <div className="col-12">
              <h5 className="mb-3">Pedidos por Estado</h5>
            </div>

            {dashboardData.orders_by_status && dashboardData.orders_by_status.length > 0 ? (
              dashboardData.orders_by_status.map((status, index) => {
                const style = getStatusStyle(status.status_description);
                const Icon = style.icon;
                
                return (
                  <div key={status.status_id || index} className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                    <div className="stats-card">
                      <div className={`stats-icon bg-${style.color}`}>
                        <Icon />
                      </div>
                      <div className="stats-content">
                        <h6 className="stats-label text-uppercase">{status.status_description}</h6>
                        <h3 className="stats-value">{status.orders_count}</h3>
                        <small className="text-muted">
                          {status.orders_count === 1 ? 'pedido' : 'pedidos'}
                        </small>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-12">
                <div className="alert alert-info">
                  No hay estados de pedidos configurados o no hay pedidos en el período seleccionado.
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {!loading && !dashboardData && (
        <div className="text-center py-5">
          <p className="text-muted">Selecciona un rango de fechas para ver el dashboard</p>
        </div>
      )}
    </div>
  );
}

export default DashboardHomePage;
        